/**
 * Adds zero padding to a number or string to achieve a desired length.
 *
 * @param {number | string} num - The number or string to be padded. For example, 5 or "5".
 * @param {number} length - The desired length of the output string. For example, 3.
 * @returns {string} - The zero-padded number as a string. For example, "005".
 *
 * @example
 * zeroPad(5, 3); // Returns "005"
 * zeroPad("5", 3); // Returns "005"
 */
export const zeroPad = (num: number | string, length: number) => {
  const numStr = num.toString(); // Convert the number or string to a string
  const zerosNeeded = length - numStr.length; // Calculate how many zeros are needed
  const zeros = "0".repeat(zerosNeeded); // Create a string of the necessary number of zeros

  return zeros + numStr; // Prepend zeros to the original number string
};

/**
 * Removes leading zeros from a string.
 *
 * @param {string} value - The string from which leading zeros should be removed.
 * @returns {string} - The string with leading zeros removed.
 *
 * @example
 * removeLeadingZero("000123"); // Returns "123"
 */
export const removeLeadingZero = (value: string) => {
  return value.replace(/^0+/, ""); // Use regex to replace leading zeros with an empty string
};

/**
 * Checks if a value is a number.
 *
 * @param {unknown} value - The value to be checked.
 * @returns {boolean} - True if the value is a number, otherwise false.
 *
 * @example
 * isNumber(123); // Returns true
 * isNumber("123"); // Returns false
 */
export const isNumber = (value: unknown): value is number => {
  const valueNumber = Number(value); // Convert the value to a number
  return !isNaN(valueNumber); // Check if the result is not NaN
};

/**
 * Converts a value to a number.
 *
 * @param {unknown} value - The value to be converted.
 * @returns {number} - The converted number, or 0 if the value is not a number.
 *
 * @example
 * toNumber(123); // Returns 123
 * toNumber("123"); // Returns 123
 * toNumber("abc"); // Returns 0
 */
export const toNumber = (value: unknown) => {
  if (isNumber(value)) return Number(value); // Return the value if it's a number
  return 0; // Return 0 if the value is not a number
};

export const formatNumber = (value: number | string): string => {
  try {
    if (typeof value === "string") {
      const sValue = Number(value);
      return `${sValue.toLocaleString("en-GB")}`;
    }
    return `${value.toLocaleString("en-GB")}`;
  } catch {
    return value.toString();
  }
};

/**
 * Formats an ID with a prefix and zero-padded number.
 *
 * @param {number | string | null | undefined} id - The ID to format. Can be a number, string, null, or undefined.
 * @param {string} prefix - The prefix to add before the number (e.g., "R" for RSO, "C" for Company).
 * @param {number} length - The desired length of the number part (default: 10).
 * @returns {string} - The formatted ID string (e.g., "R0123456789" or "C0123456789") or empty string if id is null/undefined.
 *
 * @example
 * formatIdWithPrefix(123456789, "R", 10); // Returns "R0123456789"
 * formatIdWithPrefix("123456789", "C", 10); // Returns "C0123456789"
 * formatIdWithPrefix(null, "R"); // Returns ""
 */
export const formatIdWithPrefix = (id: number | string | null | undefined, prefix: string, length: number = 10): string => {
  if (id === null || id === undefined) return "";
  const numStr = id.toString();
  const paddedNumber = zeroPad(numStr, length);
  return `${prefix}${paddedNumber}`;
};

/**
 * Formats an RSO ID with prefix "R" and zero-padded number.
 *
 * @param {number | string | null | undefined} id - The ID to format. Can be a number, string, null, or undefined.
 * @param {number} length - The desired length of the number part (default: 10).
 * @returns {string} - The formatted ID string (e.g., "R0123456789") or empty string if id is null/undefined.
 *
 * @example
 * formatRSOId(123456789, 10); // Returns "R0123456789"
 * formatRSOId("123456789", 10); // Returns "R0123456789"
 * formatRSOId(null); // Returns ""
 */
export const formatRSOId = (id: number | string | null | undefined, length: number = 10): string => {
  return formatIdWithPrefix(id, "R", length);
};

/**
 * Formats a Company ID with prefix "C" and zero-padded number.
 *
 * @param {number | string | null | undefined} id - The ID to format. Can be a number, string, null, or undefined.
 * @param {number} length - The desired length of the number part (default: 10).
 * @returns {string} - The formatted ID string (e.g., "C0123456789") or empty string if id is null/undefined.
 *
 * @example
 * formatCompanyId(123456789, 10); // Returns "C0123456789"
 * formatCompanyId("123456789", 10); // Returns "C0123456789"
 * formatCompanyId(null); // Returns ""
 */
export const formatCompanyId = (id: number | string | null | undefined, length: number = 10): string => {
  return formatIdWithPrefix(id, "C", length);
};

/**
 * Formats a Support ID with prefix "S" and zero-padded number.
 *
 * @param {number | string | null | undefined} id - The ID to format. Can be a number, string, null, or undefined.
 * @param {number} length - The desired length of the number part (default: 10).
 * @returns {string} - The formatted ID string (e.g., "S0123456789") or empty string if id is null/undefined.
 *
 * @example
 * formatSupportId(123456789, 10); // Returns "S0123456789"
 * formatSupportId("123456789", 10); // Returns "S0123456789"
 * formatSupportId(null); // Returns ""
 */
export const formatSupportId = (id: number | string | null | undefined, length: number = 10): string => {
  return formatIdWithPrefix(id, "S", length);
};
